/**
 * Shared payroll salary breakdown helpers (CTC → components).
 */

export const calculatePF = (employee, basicSalary) => {
  if (!employee || !basicSalary) return 0;
  const pfType = employee.pf_calculation_type || 'attendance_based';
  const basic = parseFloat(basicSalary) || 0;
  if (pfType === 'none') return 0;
  if (pfType === 'fixed') return parseFloat(employee.fixed_pf_amount || 0);
  let pfAmount = Math.round(basic * 0.12);
  if (pfType === 'attendance_based_max_amount' && pfAmount > 1800) pfAmount = 1800;
  return pfAmount;
};

export const buildCtcBreakdown = (annualCtc, employee) => {
  const annualSalary = parseFloat(annualCtc || 0);
  if (!annualSalary || Number.isNaN(annualSalary)) return null;

  const monthlySalary = annualSalary / 12;
  const basic = Math.round(monthlySalary * 0.45);
  const hra = Math.round(monthlySalary * 0.18);
  const specialAllowance = Math.round(monthlySalary * 0.12);
  const otherAllowance = Math.round(monthlySalary - basic - hra - specialAllowance);
  const bonus = Math.round(basic * 0.0833);
  const overtime = 0;
  const employeePf = calculatePF(employee, basic);
  const profTax = 200;
  const medicalInsurance = 350;
  const taxDeduction = 0;

  const allowances = hra + otherAllowance + specialAllowance + overtime + bonus;
  const grossSalary = basic + allowances;
  const esi = grossSalary <= 21000 ? Math.round(grossSalary * 0.0075) : 0;
  const totalDeductions = employeePf + profTax + taxDeduction + medicalInsurance + esi;
  const netSalary = grossSalary - totalDeductions;

  return {
    basic_salary: basic,
    hra,
    other_allowance: otherAllowance,
    special_allowance: specialAllowance,
    overtime_pay: overtime,
    bonus,
    employee_pf: employeePf,
    professional_tax: profTax,
    tax_deduction: taxDeduction,
    medical_insurance: medicalInsurance,
    allowances,
    gross_salary: grossSalary,
    esi,
    net_salary: netSalary,
  };
};

export const resolveDeploymentEmployeeDbId = (dep, employeeList) => {
  if (!dep) return null;
  const depEmpId = dep.employee_id;
  if (!depEmpId && !dep.employee_code) return null;

  const byUuid = employeeList.find((e) => e.id === depEmpId);
  if (byUuid) return byUuid.id;

  const code = dep.employee_code || depEmpId;
  const byCode = employeeList.find((e) => e.employee_id === code);
  return byCode?.id || null;
};

const ELIGIBLE_OFFER_STATUSES = ['accepted', 'moved deployment', 'sent'];

export const buildOfferCtcMap = (offers, employees) => {
  const map = new Map();
  if (!Array.isArray(offers) || !Array.isArray(employees)) return map;

  const eligible = offers.filter((o) => {
    const status = (o.status || '').toString().toLowerCase();
    return ELIGIBLE_OFFER_STATUSES.includes(status) && parseFloat(o.offered_salary || 0) > 0;
  });

  employees.forEach((emp) => {
    const matches = eligible.filter((o) => {
      if (o.employee_id === emp.id || o.employee_id === emp.employee_id) return true;
      if (emp.email && (o.applicant_email || o.email || '').toLowerCase() === emp.email.toLowerCase()) {
        return true;
      }
      return false;
    });

    if (matches.length > 0) {
      const latest = [...matches].sort(
        (a, b) =>
          new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
      )[0];
      map.set(emp.id, parseFloat(latest.offered_salary));
    }
  });

  return map;
};

export const mapPayrollAmounts = (employee, payrollRecord, annualCtc) => {
  const hasRecord =
    payrollRecord && parseFloat(payrollRecord.gross_salary || payrollRecord.basic_salary || 0) > 0;

  if (hasRecord) {
    const basic = parseFloat(payrollRecord.basic_salary || 0);
    const allowances =
      parseFloat(payrollRecord.allowances || 0) +
      parseFloat(payrollRecord.hra || 0) +
      parseFloat(payrollRecord.other_allowance || 0) +
      parseFloat(payrollRecord.special_allowance || 0) +
      parseFloat(payrollRecord.overtime_pay || 0) +
      parseFloat(payrollRecord.bonus || 0);
    const gross = parseFloat(payrollRecord.gross_salary || 0) || basic + allowances;
    const pf = parseFloat(payrollRecord.employee_pf || 0);
    const pt = parseFloat(payrollRecord.professional_tax || 0);
    const tds = parseFloat(payrollRecord.tax_deduction || 0);
    const esi = gross <= 21000 ? Math.round(gross * 0.0075) : 0;

    return {
      basicSalary: basic,
      allowances,
      pf,
      esi,
      pt,
      tds,
      medicalInsurance: parseFloat(payrollRecord.medical_insurance || 0),
      grossSalary: gross,
      netPay: parseFloat(payrollRecord.net_salary || 0),
      isProjected: false,
    };
  }

  const ctc =
    annualCtc ||
    (employee?.salary && parseFloat(employee.salary) > 0 ? parseFloat(employee.salary) * 12 : null);

  if (!ctc) {
    return {
      basicSalary: 0,
      allowances: 0,
      pf: 0,
      esi: 0,
      pt: 0,
      tds: 0,
      medicalInsurance: 0,
      grossSalary: 0,
      netPay: 0,
      isProjected: false,
    };
  }

  const breakdown = buildCtcBreakdown(ctc, employee);
  if (!breakdown) {
    return {
      basicSalary: 0,
      allowances: 0,
      pf: 0,
      esi: 0,
      pt: 0,
      tds: 0,
      medicalInsurance: 0,
      grossSalary: 0,
      netPay: 0,
      isProjected: false,
    };
  }

  return {
    basicSalary: breakdown.basic_salary,
    allowances: breakdown.allowances,
    pf: breakdown.employee_pf,
    esi: breakdown.esi,
    pt: breakdown.professional_tax,
    tds: breakdown.tax_deduction,
    medicalInsurance: breakdown.medical_insurance,
    grossSalary: breakdown.gross_salary,
    netPay: breakdown.net_salary,
    isProjected: true,
  };
};

export const indexPayrollRecord = (records) => {
  const map = new Map();
  records.forEach((record) => {
    const keys = new Set([record.employee_id, record.employee_number].filter(Boolean));
    keys.forEach((key) => {
      const existing = map.get(key);
      if (
        !existing ||
        new Date(record.pay_period_end) > new Date(existing.pay_period_end)
      ) {
        map.set(key, record);
      }
    });
  });
  return map;
};

export const findPayrollRecord = (payrollMap, employee) => {
  if (!employee) return null;
  return payrollMap.get(employee.id) || payrollMap.get(employee.employee_id) || null;
};
